// The MIT License (MIT)
//
// Copyright (c) 2016-2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import angular from 'angular';
import epfl_authgeoportailBase from '../epfl_authgeoportailmodule.js';
import epfl_authgeoportailCommon from './Common.js';
import epfl_authgeoportailLoaderDirective from '../loader/directive.js';
import epfl_authgeoportailFloorComponent from '../floor/component.js';
import epfl_authgeoportailPrintComponent from '../print/component.js';
import epfl_authgeoportailShortcutsComponent from '../shortcuts/component.js';
import epfl_authgeoportailSearchComponent from '../search/component.js';
import epfl_authgeoportailRoutingComponent from '../routing/component.js';
import epfl_authgeoportailRoutingroadmapComponent from '../routingroadmap/component.js';
import epfl_authgeoportailSelectedlayersComponent from '../selectedlayers/component.js';
import epfl_authgeoportailSearchLoadingDirective from '../searchloading/directive.js';
import epfl_authgeoportailBodyCssTogglerDirective from '../bodycsstoggler/directive.js';
import gmfAbstractMobileController, {
  AbstractMobileController,
} from 'gmf/controllers/AbstractMobileController.js';
import olControlMousePosition from 'ol/control/MousePosition.js';
import * as olCoordinate from 'ol/coordinate.js';

import './sass/main.scss';

/**
 * @private
 */
class Controller extends AbstractMobileController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {angular.ITimeoutService} $timeout Angular timeout service.
   * @param {import("ngeo/statemanager/Location.js").StatemanagerLocation} ngeoLocation ngeo location service.
   * @param {import("ngeo/map/BackgroundLayerMgr.js").MapBackgroundLayerManager} ngeoBackgroundLayerMgr Background layer manager.
   * @param {string} epflBuildingsUrl EPFL buildings URL.
   * @param {string} currentInterfaceUrl The current interface URL.
   */
  constructor(
    $scope,
    $injector,
    $timeout,
    ngeoLocation,
    ngeoBackgroundLayerMgr,
    epflBuildingsUrl,
    currentInterfaceUrl,
  ) {
    super($scope, $injector);

    // Fix https://jira.camptocamp.com/browse/GSEPF-163
    ngeoLocation.deleteParam('tree_groups');

    /**
     * @type {string|undefined}
     */
    this.room;
    const possibleRoom = ngeoLocation.getParam('room') || '';
    if (possibleRoom && possibleRoom.indexOf('=') === 0) {
      this.room = possibleRoom.replace('=', ''); // for exact match, remove extra egal sign
    }

    this.epflBuildingsUrl = epflBuildingsUrl;

    this.currentInterfaceUrl = currentInterfaceUrl;

    // apply dimensions to any newly set background layer
    ngeoBackgroundLayerMgr.on(
      'change',
      () => {
        ngeoBackgroundLayerMgr.updateDimensions(this.map, this.dimensions);
      },
      this,
    );

    // add mouse position control
    this.map.addControl(
      new olControlMousePosition({
        coordinateFormat: olCoordinate.createStringXY(0),
      }),
    );

    /**
     * @public
     */
    this.timeout = $timeout;

    /**
     * @type {epfl_authgeoportail.Common}
     * @export
     */
    this.common = new epfl_authgeoportailCommon(this, $scope, $injector);

    /**
     * Value used to activate the print mode and deactive some component on the map
     * and the possibilty to query the map
     * @type {boolean}
     * @export
     */
    this.printModeActive = false;

    /**
     * Value used only to say to the print that the map is rightly positionned to
     * be printed.
     * @type {boolean}
     * @export
     */
    this.printAreaSelected = false;

    /**
     * @type {number}
     * @export
     */
    this.copyrightYear = new Date().getFullYear();

    /**
     * If the routing roadmap is collapsed or not
     * @type {boolean}
     * @export
     */
    this.routingRoadmapCollapsed = false;

    // Active the print when you open the panel and the print panel is already
    // activate.
    $scope.$watch(
      () => this.rightNavVisible,
      (newVal, oldVal) => {
        if (newVal && newVal !== oldVal && this.printModeActive === false) {
          const printPanelActive = jQuery('.gmf-mobile-nav-right epfl-print.gmf-mobile-nav-active');
          if (printPanelActive.length > 0) {
            this.printModeActive = true;
          }
        }
      },
    );

    // Autofocus on typeahead search bar
    setTimeout(() => {
      document.querySelector('.twitter-typeahead input.tt-input').focus();
    }, 0);
  }

  /**
   * Set the printAreaSelected and visibility of the right panel to the given
   * value.
   * @param {boolean} value The value.
   * @export
   */
  setPrintAreaSelected(value) {
    const val = value === true;
    this.printAreaSelected = val;
    this.rightNavVisible = val;
  }

  /**
   * Set the printModeActive value to the given value. If the print mode is
   * deactivated, set also printAreaSelected value to false.
   * @param {boolean} value The value.
   * @export
   */
  setPrintMode(value) {
    const val = value === true;
    if (!val) {
      this.printAreaSelected = false;
    }
    this.printModeActive = val;
  }

  /**
   * Reset url
   * @export
   */
  resetUrl() {
    window.location = this.currentInterfaceUrl;
  }
}
Controller.$inject = [
  '$scope',
  '$injector',
  '$timeout',
  'ngeoLocation',
  'ngeoBackgroundLayerMgr',
  'epflBuildingsUrl',
  'currentInterfaceUrl',
];

/**
 * @hidden
 */
const gmfModule = angular.module('Appmain', [
  epfl_authgeoportailBase.name,
  epfl_authgeoportailCommon.module.name,
  gmfAbstractMobileController.name,
  epfl_authgeoportailLoaderDirective.name,
  epfl_authgeoportailFloorComponent.name,
  epfl_authgeoportailPrintComponent.name,
  epfl_authgeoportailShortcutsComponent.name,
  epfl_authgeoportailSearchComponent.name,
  epfl_authgeoportailRoutingComponent.name,
  epfl_authgeoportailRoutingroadmapComponent.name,
  epfl_authgeoportailSelectedlayersComponent.name,
  epfl_authgeoportailSearchLoadingDirective.name,
  epfl_authgeoportailBodyCssTogglerDirective.name,
]);

gmfModule.run([
  '$templateCache',
  /**
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    $templateCache.put('gmf/layertree', require('../layertree/layertree.html'));
    $templateCache.put('gmf/query/windowComponent', require('../displayquerywindow/displayquerywindow.html'));
  },
]);

gmfModule.constant('redirectUrl', '');

gmfModule.controller('MainController', Controller);

export default gmfModule;
